import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Send,
    Paperclip,
    CheckCircle,
    User,
    Calendar,
    MessageSquare,
    Clock
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const TicketDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [ticket, setTicket] = useState(null);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [supportAgents, setSupportAgents] = useState([]);
    const fileInputRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const fetchSupportAgents = async () => {
            try {
                const { data } = await api.get('/users', { params: { role: 'support', limit: 100 } });
                if (data.success) {
                    setSupportAgents(data.data);
                }
            } catch (err) {
                console.error('Failed to fetch support agents:', err);
            }
        };
        fetchSupportAgents();
    }, []);

    const getAttachmentUrl = (file) => {
        if (!file) return '#';
        
        // 1. If it's an HTTP/S link already, return it directly
        const url = file.url || file.publicUrl;
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            return url;
        }
        if (file.path && (file.path.startsWith('http://') || file.path.startsWith('https://'))) {
            return file.path;
        }
        
        // 2. Extract the file name
        let filename = file.filename;
        if (!filename && file.path) {
            filename = file.path.split(/[/\\]/).pop();
        }
        
        if (!filename) return '#';
        
        // 3. Determine base URL (student tickets are uploaded to the student backend)
        const isStudentTicket = ticket?.userModel === 'Student' || 
                                ticket?.user?.role === 'student' || 
                                (ticket?.userId && ticket?.userId.role === 'student');
        
        const backendBaseUrl = isStudentTicket
            ? (process.env.REACT_APP_STUDENT_API_URL || `http://${window.location.hostname}:5000`)
            : (process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`);
            
        const cleanBaseUrl = backendBaseUrl.endsWith('/') ? backendBaseUrl.slice(0, -1) : backendBaseUrl;
        return `${cleanBaseUrl}/uploads/${filename}`;
    };

    const handleFileSelect = (e) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files));
        }
    };

    useEffect(() => {
        fetchTicket();
    }, [id]);

    useEffect(() => {
        scrollToBottom();
    }, [ticket?.responses]);

    useEffect(() => {
        const parentMain = containerRef.current?.parentElement;
        if (parentMain) {
            const originalOverflow = parentMain.style.overflow;
            const originalPadding = parentMain.style.padding;
            parentMain.style.overflow = 'hidden';
            parentMain.style.padding = '0';
            return () => {
                parentMain.style.overflow = originalOverflow;
                parentMain.style.padding = originalPadding;
            };
        }
    }, [ticket]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchTicket = async () => {
        try {
            const { data } = await api.get(`/tickets/${id}`);
            if (data.success) {
                setTicket(data.data);
            }
        } catch (error) {
            if (!navigator.onLine) {
                toast.error('No internet connection. Please check your network and try again.');
            } else {
                toast.error('Failed to load ticket details');
            }
            navigate('/support');
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        try {
            const { data } = await api.put(`/tickets/${id}/status`, { status: newStatus });
            if (data.success) {
                setTicket(prev => ({ ...prev, status: newStatus }));
                toast.success(`Status updated to ${newStatus}`);
            }
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleAssignTicket = async (adminId) => {
        try {
            const { data } = await api.put(`/tickets/${id}/assign`, { adminId });
            if (data.success) {
                setTicket(prev => ({ ...prev, assignedTo: adminId ? { _id: adminId } : null }));
                toast.success(adminId ? 'Ticket assigned successfully' : 'Ticket unassigned');
            }
        } catch (error) {
            toast.error('Failed to assign ticket');
        }
    };

    const messagesEndRef = useRef(null);

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!reply.trim() && selectedFiles.length === 0) return;

        try {
            setSending(true);
            let responseData;

            if (selectedFiles.length > 0) {
                const formData = new FormData();
                formData.append('message', reply);
                selectedFiles.forEach(file => {
                    formData.append('attachments', file);
                });
                const { data } = await api.post(`/tickets/${id}/response`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                responseData = data;
            } else {
                const { data } = await api.post(`/tickets/${id}/response`, { message: reply });
                responseData = data;
            }

            if (responseData.success) {
                setTicket(prev => ({
                    ...prev,
                    responses: [...prev.responses, responseData.data]
                }));
                setReply('');
                setSelectedFiles([]);
                toast.success('Reply sent successfully');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const getPriorityBadge = (priority) => {
        const styles = {
            low: 'bg-slate-100 text-slate-700 border-slate-200',
            medium: 'bg-blue-100 text-blue-800 border-blue-200',
            high: 'bg-orange-100 text-orange-800 border-orange-200',
            urgent: 'bg-rose-100 text-rose-800 border-rose-200'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize border ${styles[priority] || styles.low}`}>
                {priority} Priority
            </span>
        );
    };

    const getStatusBadge = (status) => {
        const styles = {
            open: 'bg-blue-100 text-blue-800',
            in_progress: 'bg-yellow-100 text-yellow-800',
            resolved: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${styles[status] || styles.open}`}>
                {status?.replace('_', ' ')}
            </span>
        );
    };

    if (!ticket) return null;

    return (
        <div ref={containerRef} className="h-full flex flex-col overflow-hidden bg-[#f4f6fb]">
            <div className="max-w-6xl w-full mx-auto p-6 flex flex-col h-full min-h-0 space-y-4">
                {/* Header */}
                <header className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between shrink-0 z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/support')}
                        className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-bold text-gray-900 line-clamp-1">{ticket.subject}</h1>
                            <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">#{ticket.ticketId}</span>
                            {getStatusBadge(ticket.status)}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-1.5 text-sm text-gray-500">
                            <span className="flex items-center gap-1.5" title="Requester">
                                <User size={14} className="text-gray-400" /> <span className="font-medium text-gray-700">Requester:</span> {ticket.user?.fullName || ticket.user?.name || 'Anonymous'}
                            </span>
                            <span className="flex items-center gap-1.5" title="Created date">
                                <Calendar size={14} className="text-gray-400" /> <span className="font-medium text-gray-700">Created:</span> {new Date(ticket.createdAt).toLocaleDateString()}
                            </span>
                            <span className="capitalize px-2.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-xs font-semibold">{ticket.category}</span>
                            {ticket.priority && getPriorityBadge(ticket.priority)}
                            <span className="flex items-center gap-1.5" title="Assignee">
                                <User size={14} className="text-gray-400" /> 
                                <span className="font-medium text-gray-700 mr-1">Assignee:</span>
                                <span className="font-semibold text-gray-800">
                                    {ticket.assignedTo?.fullName || 'Unassigned'}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="w-full flex flex-col h-full min-h-0 space-y-4">

                    {/* Original Ticket Description */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-250 p-6 relative shrink-0">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                {ticket.user?.profileImage || ticket.user?.avatar ? (
                                    <img src={ticket.user?.profileImage || ticket.user?.avatar} className="w-10 h-10 rounded-full object-cover" alt="" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                        {ticket.user?.fullName?.charAt(0) || ticket.user?.name?.charAt(0)}
                                    </div>
                                )}
                                <div>
                                    <p className="font-semibold text-gray-900">{ticket.user?.fullName || ticket.user?.name}</p>
                                    {ticket.user?.college?.collegeName && (
                                        <p className="text-xs font-semibold text-[#1a3884] leading-relaxed break-words max-w-md">{ticket.user.college.collegeName}</p>
                                    )}
                                    <p className="text-xs text-gray-500">Reported on {new Date(ticket.createdAt).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                        <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100 max-h-[150px] overflow-y-auto">
                            {ticket.description}
                        </div>
                        {/* Attachments if any */}
                        {ticket.attachments && ticket.attachments.length > 0 && (
                            <div className="mt-4 flex flex-col gap-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Attachments</p>
                                <div className="flex flex-wrap gap-3">
                                    {ticket.attachments.map((file, idx) => {
                                        const fileUrl = getAttachmentUrl(file);
                                        const isImage = file.mimetype?.startsWith('image/') || 
                                                        /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl || file.originalName || file.name || '');
                                        return (
                                            <div key={idx} className="flex flex-col gap-1">
                                                {isImage ? (
                                                    <a 
                                                        href={fileUrl} 
                                                        target="_blank" 
                                                        rel="noreferrer" 
                                                        className="block w-24 h-24 rounded-lg overflow-hidden border border-gray-200 hover:border-[#1a3884] transition-all shadow-sm group relative"
                                                    >
                                                        <img 
                                                            src={fileUrl} 
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                                                            alt={file.originalName || file.name || file.filename || 'Attachment'} 
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                            <span className="text-[10px] text-white font-medium bg-black/60 px-2 py-0.5 rounded">View</span>
                                                        </div>
                                                    </a>
                                                ) : (
                                                    <a 
                                                        href={fileUrl} 
                                                        target="_blank" 
                                                        rel="noreferrer" 
                                                        className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100"
                                                    >
                                                        <Paperclip size={14} /> 
                                                        <span className="max-w-[120px] truncate">{file.originalName || file.name || file.filename || 'Attachment'}</span>
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Conversation History */}
                    <div className="flex-1 overflow-y-auto space-y-6 pr-2 scrollbar-thin scrollbar-thumb-gray-300 min-h-0">
                        {ticket.responses?.length > 0 ? (
                            <div className="space-y-6">
                                <div className="relative flex items-center justify-center">
                                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                                    <div className="relative bg-gray-50 px-4 text-xs font-semibold text-gray-500 uppercase tracking-widest">Conversation History</div>
                                </div>

                                {ticket.responses.map((response, index) => (
                                    <div
                                        key={index}
                                        className={`flex ${response.senderRole === 'user' ? 'justify-start' : 'justify-end'}`}
                                    >
                                        <div className={`max-w-[80%] rounded-2xl p-5 shadow-sm ${response.senderRole === 'user'
                                            ? 'bg-white border border-gray-200 rounded-tl-none'
                                            : 'bg-[#1a3884] text-white rounded-tr-none'
                                            }`}>
                                            <div className="flex items-center justify-between gap-4 mb-2">
                                                <span className={`text-xs font-bold uppercase ${response.senderRole === 'user' ? 'text-gray-500' : 'text-blue-200'}`}>
                                                    {response.sender?.name || (response.senderRole === 'admin' ? 'Support Agent' : 'User')}
                                                </span>
                                                <span className={`text-[10px] ${response.senderRole === 'user' ? 'text-gray-400' : 'text-blue-200'}`}>
                                                    {new Date(response.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <p className={`text-sm whitespace-pre-wrap ${response.senderRole === 'user' ? 'text-gray-700' : 'text-white'}`}>
                                                {response.message}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-xl border border-gray-200 p-6">
                                No messages in conversation yet.
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </main>
            </div>
        </div>
    );
};

export default TicketDetail;
